import React from "react";
import { useTranslation } from "next-i18next";
import ImgTag from "@/components/Helpers/ImgTag";
import { getEwHeaderConfig } from "./ewModel";

const InfoCircleIcon = ({ className }) => (
  <svg
    width="19.167"
    height="19.167"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.5" />
    <rect
      x="9.25"
      y="8.5"
      width="1.5"
      height="6"
      rx="0.75"
      fill="currentColor"
    />
    <circle cx="10" cy="6.25" r="0.875" fill="currentColor" />
  </svg>
);

/**
 * EW selector header — Almanea logo + title, the Tawuniya branding badge, and
 * the "more info" trigger. Title / label / logos come from the BE-localised
 * `configuration` block (see getEwHeaderConfig), each degrading to the i18n
 * string / local asset.
 *
 * compact (mini-cart drawer) forces the small variant: the drawer is narrow but
 * sits on a desktop viewport, so md: breakpoints don't help — `md()` drops the
 * md: upgrades and the badge keeps the short "by" label.
 */
const EWSelectorHeader = ({ configuration, isRTL, onMoreInfo, compact }) => {
  const { t } = useTranslation("extended_warranty");
  const { title, moreInfoLabel, almaneaLogo, tawniaLogo } =
    getEwHeaderConfig(configuration);
  const headerTitle =
    title || t("safeguardTitle", { defaultValue: "Almanea Safeguard" });
  const moreInfoText =
    moreInfoLabel || t("moreInfo", { defaultValue: "more info" });
  const md = (cls) => (compact ? "" : cls);

  return (
    <div className="flex items-center gap-[5px] h-[40px]">
      <div className="flex items-center gap-2 min-w-0">
        <div
          className={`flex items-center justify-center w-5 h-5 rounded-full overflow-hidden shrink-0 ${md(
            "md:w-6 md:h-6",
          )}`}
        >
          <ImgTag
            src={almaneaLogo}
            alt="Almanea"
            width={24}
            height={24}
            className="object-contain w-full h-full"
          />
        </div>
        <h2
          className={`text-[#00269A] font-[600] leading-[40px] truncate ${
            compact ? "text-[14px]" : "text-[14px] md:text-[17.515px]"
          }`}
          style={{
            fontFamily: isRTL ? "var(--font-noto, 'Noto Sans Arabic', sans-serif)" : "'Poppins', sans-serif",
          }}
        >
          {headerTitle}
        </h2>
      </div>

      {/* Tawuniya branding badge — rendered only when the API returns a logo.
          Non-compact desktop shows the full "Powered by" label, everywhere else
          the short "by". Label + logo order flips with the locale dir. */}
      {tawniaLogo && (
        <div
          className={`flex items-center gap-[5px] shrink-0 rounded-[48px] bg-white px-[8px] py-[2px] ${md(
            "md:gap-[10px] md:px-[11px]",
          )}`}
        >
          <span
            className={`${
              isRTL ? "font-noto" : "font-poppins"
            } text-[10px] font-[500] leading-[20px] text-[#303151] whitespace-nowrap ${md(
              "md:leading-[32px]",
            )}`}
          >
            <span className={md("md:hidden")}>
              {t("poweredByShort", { defaultValue: "by" })}
            </span>
            <span className={compact ? "hidden" : "hidden md:inline"}>
              {t("poweredBy", { defaultValue: "Powered by" })}
            </span>
          </span>
          <ImgTag
            src={tawniaLogo}
            alt="Tawuniya"
            width={56}
            height={18}
            className={`object-contain shrink-0 ${
              compact
                ? "w-[40px] h-[13px]"
                : "w-[44px] h-[14px] md:w-[56px] md:h-[18px]"
            }`}
          />
        </div>
      )}

      {/* More info trigger */}
      <button
        type="button"
        onClick={onMoreInfo}
        aria-label={t("moreInfoAria", {
          defaultValue: "More information about this warranty",
        })}
        className={`flex items-center gap-1 shrink-0 ms-auto hover:opacity-75 transition-opacity ${md(
          "md:gap-2",
        )}`}
      >
        <span
          className={`text-[#6B7280] font-[500] leading-[40px] text-[12px] ${md(
            "md:text-[14px]",
          )}`}
          style={{
            fontFamily: isRTL ? "var(--font-noto, 'Noto Sans Arabic', sans-serif)" : "'Poppins', sans-serif",
          }}
        >
          {moreInfoText}
        </span>
        <InfoCircleIcon className="text-[#00269A] shrink-0" />
      </button>
    </div>
  );
};

export default EWSelectorHeader;
